import { useState, type FormEvent } from 'react';
import { toUserMessage } from '@platform/api-client';
import { formatKstDate } from '@platform/datetime';

import { Alert, FormField } from '@/shared/ui';
import {
  useDeferOnboardingPhone,
  useOnboardingPhone,
  useSendPhoneOtp,
} from '../../api/use-auth.mutation';
import { PHONE_OTP_TTL_SECONDS } from '../../constants/cooldown.constant';
import { CodeVerifier } from './CodeVerifier';
import { PrimaryButton, StepHeading, TextButton } from './OnboardingUi';

const PHONE_MAX_DIGITS = 11;
const onlyDigits = (v: string) =>
  v.replace(/\D/g, '').slice(0, PHONE_MAX_DIGITS);
const formatPhone = (digits: string) =>
  digits.replace(/^(\d{3})(\d{3,4})(\d{4})$/, '$1-$2-$3');
const isMobile = (digits: string) => /^01[016789]\d{7,8}$/.test(digits);

/**
 * 3단계(마지막) — 등록된 번호로 인증번호를 받아 확인한다. 확인이 곧 완료다(버튼을 더 누르지 않는다).
 * 관리자가 번호를 잘못 넣었을 수 있어 바꿀 수 있게 둔다 — 서버가 org 안 중복을 다시 본다.
 * 구버전에서 여러 계정을 나눠 쓰던 이관 계정은 마감 전까지 "나중에 하기"로 넘길 수 있다(`deferUntil`).
 */
export function PhoneVerifyStep({
  initialPhone,
  deferUntil,
}: {
  initialPhone: string;
  /** 유예 마감(UTC ISO). 넘길 수 없으면 undefined. */
  deferUntil?: string;
}) {
  const [phone, setPhone] = useState(onlyDigits(initialPhone));
  const [editing, setEditing] = useState(!isMobile(onlyDigits(initialPhone)));
  const [phoneError, setPhoneError] = useState<string>();
  const [sentAt, setSentAt] = useState<number | null>(null);
  const send = useSendPhoneOtp();
  const complete = useOnboardingPhone();
  const defer = useDeferOnboardingPhone();

  const requestCode = () => {
    if (!isMobile(phone)) {
      setPhoneError('휴대폰 번호를 정확히 입력해 주세요.');
      return;
    }
    complete.reset();
    send.mutate(phone, {
      onSuccess: () => {
        setEditing(false);
        setSentAt(Date.now());
      },
    });
  };

  const sendError = send.error && (
    <Alert tone="error">{toUserMessage(send.error)}</Alert>
  );

  if (sentAt) {
    return (
      <div>
        <StepHeading title="인증번호를 입력해 주세요">
          <b className="font-semibold text-neutral-950 dark:text-neutral-100">
            {formatPhone(phone)}
          </b>
          (으)로 보낸 6자리 인증번호를 입력하면 설정이 완료됩니다.{' '}
          <TextButton
            onClick={() => {
              setSentAt(null);
              setEditing(true);
              send.reset();
              complete.reset();
            }}
          >
            번호 변경
          </TextButton>
        </StepHeading>
        <CodeVerifier
          key={sentAt}
          sentAt={sentAt}
          ttlSeconds={PHONE_OTP_TTL_SECONDS}
          onSubmit={(code) => complete.mutate({ phone, code })}
          submitting={complete.isPending}
          submitLabel="인증하고 완료"
          error={complete.error ? toUserMessage(complete.error) : undefined}
          onResend={requestCode}
          resending={send.isPending}
        />
        {sendError && <div className="mt-3">{sendError}</div>}
      </div>
    );
  }

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    requestCode();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5" noValidate>
      <StepHeading title="휴대폰 번호를 인증해 주세요">
        마지막 단계입니다. 본인 확인을 위해 인증번호를 보내 드립니다.
      </StepHeading>

      {editing ? (
        <FormField
          label="휴대폰 번호"
          type="tel"
          name="phone"
          inputMode="numeric"
          autoComplete="tel"
          autoFocus
          placeholder="01012345678"
          value={phone}
          onChange={(e) => {
            setPhone(onlyDigits(e.target.value));
            setPhoneError(undefined);
            if (send.error) send.reset();
          }}
          error={phoneError}
          hint="숫자만 입력해 주세요."
        />
      ) : (
        <div className="flex items-center justify-between gap-3 rounded-lg bg-neutral-100 px-4 py-3 dark:bg-white/5">
          <span className="text-sm font-semibold tabular-nums text-neutral-950 dark:text-neutral-100">
            {formatPhone(phone)}
          </span>
          <TextButton onClick={() => setEditing(true)}>번호 변경</TextButton>
        </div>
      )}

      {sendError}
      <PrimaryButton type="submit" disabled={send.isPending}>
        {send.isPending ? '보내는 중…' : '인증번호 받기'}
      </PrimaryButton>

      {deferUntil && (
        <div className="rounded-lg bg-neutral-100 px-4 py-3 text-xs text-neutral-600 dark:bg-white/5 dark:text-neutral-400">
          <p className="break-keep">
            이전 버전에서 여러 계정을 함께 쓰던 계정이라{' '}
            <b className="font-semibold text-neutral-950 dark:text-neutral-100">
              {formatKstDate(deferUntil)}
            </b>
            까지 인증을 미룰 수 있습니다. 그 뒤에는 이 계정을 실제로 쓰는 분의
            번호로 인증해야 하며, 여러 계정을 함께 관리하려면 기업 회원으로
            가입해 주세요.
          </p>
          {defer.error && (
            <div className="mt-2">
              <Alert tone="error">{toUserMessage(defer.error)}</Alert>
            </div>
          )}
          <TextButton
            className="mt-2"
            disabled={defer.isPending}
            onClick={() => defer.mutate()}
          >
            {defer.isPending ? '처리 중…' : '나중에 하기'}
          </TextButton>
        </div>
      )}
    </form>
  );
}
